/**
 * Service utilisateur unifié pour EKOSE-RX
 * Fusion de userService + userManagementService + userPreferencesService
 */

import { ApiService } from './api-services/ApiService.js';
import { logger } from '../utils/logger.js';
import type { User, DoctorProfile, PatientProfile, UserRole } from '../types/User.js';
import { handleServiceError } from './enhancedErrorService.js';
import { useAuthStore } from '../store/authStore.js';

// Interfaces unifiées
export type { User, UserRole, DoctorProfile, PatientProfile };

export interface UserStats {
  totalUsers: number;
  totalPatients: number;
  totalDoctors: number;
  totalAdmins: number;
  activeUsers: number;
  newUsersThisMonth: number;
  userGrowthRate: number;
}

export interface UserPreferences {
  // Authentification
  rememberMe: boolean;

  // Accessibilité
  accessibility: {
    highContrast: boolean;
    reducedMotion: boolean;
    fontSize: 'small' | 'medium' | 'large' | 'extra-large';
    keyboardNavigation: boolean;
    announceChanges: boolean;
  };

  // Interface utilisateur
  ui: {
    theme: 'light' | 'dark' | 'auto';
    language: 'fr' | 'en';
    sidebarCollapsed: boolean;
    dashboardLayout: 'compact' | 'comfortable' | 'spacious';
    compactMode: boolean;
  };

  // Notifications
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
    appointmentReminders: boolean;
    systemUpdates: boolean;
    marketing: boolean;
  };

  // Confidentialité
  privacy: {
    shareProfile: boolean;
    showOnlineStatus: boolean;
    allowAnalytics: boolean;
    dataRetention: '30' | '90' | '365' | 'indefinite';
  };

  // Médical (pour médecins)
  medical?: {
    defaultConsultationDuration: number;
    autoAcceptConsultations: boolean;
    showPatientHistory: boolean;
    emergencyAlerts: boolean;
  };

  // Téléconsultation
  teleconsultation: {
    defaultVideoQuality: 'low' | 'medium' | 'high';
    autoStartVideo: boolean;
    autoStartAudio: boolean;
    recordConsultations: boolean;
  };
}

export interface UserFilters {
  role?: 'PATIENT' | 'DOCTOR' | 'ADMIN';
  status?: 'active' | 'inactive';
  speciality?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'email' | 'createdAt' | 'lastLogin';
  sortOrder?: 'asc' | 'desc';
}

export interface UserCreateRequest {
  email: string;
  name: string;
  role: 'PATIENT' | 'DOCTOR' | 'ADMIN';
  password?: string;
  phone?: string;
  residence?: string;
  // Profile fields flattened to match backend
  speciality?: string;
  licenseNumber?: string;
  experienceYears?: number;
  education?: string;
  certifications?: string;
  consultationFee?: number;
  availabilityHours?: string;
  dateOfBirth?: Date;
  gender?: string;
  bloodType?: string;
  allergies?: string;
  medicalHistory?: string;
  emergencyContact?: string;
  insuranceNumber?: string;
  preferences?: Partial<UserPreferences>;
}

export interface UserUpdateRequest {
  name?: string;
  email?: string;
  role?: UserRole;
  phone?: string;
  // Profils séparés selon le rôle
  doctorProfile?: Partial<DoctorProfile>;
  patientProfile?: Partial<PatientProfile>;
  avatar?: string;
  isActive?: boolean;
  preferences?: Partial<UserPreferences>;
}

class UnifiedUserService {
  private static instance: UnifiedUserService;
  private apiService: ApiService;
  private currentUser: User | null = null;
  private userPreferences: UserPreferences | null = null;

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  static getInstance(): UnifiedUserService {
    if (!UnifiedUserService.instance) {
      UnifiedUserService.instance = new UnifiedUserService();
    }
    return UnifiedUserService.instance;
  }

  /**
   * Obtenir tous les utilisateurs avec filtres
   */
  async getUsers(filters?: UserFilters): Promise<User[]> {
    try {
      const params = new URLSearchParams();

      if (filters?.role) params.append('role', filters.role);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.speciality) params.append('speciality', filters.speciality);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.sortBy) params.append('sortBy', filters.sortBy);
      if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

      const response = await this.apiService.get<
        User[] | { status?: string; data?: User[]; users?: User[]; pagination?: unknown }
      >(`/api/admin/users?${params.toString()}`);

      let users: User[] = [];

      if (Array.isArray(response)) {
        users = response as User[];
      } else if (response && typeof response === 'object') {
        const obj = response as Record<string, unknown>;
        if (Array.isArray(obj.data)) {
          users = obj.data as User[];
        } else if (obj.data && typeof obj.data === 'object') {
          const dataObj = obj.data as Record<string, unknown>;
          if (Array.isArray(dataObj.users)) {
            users = dataObj.users as User[];
          } else if (Array.isArray(dataObj.items)) {
            users = dataObj.items as User[];
          }
        } else if (Array.isArray(obj.users)) {
          users = obj.users as User[];
        }
      }

      logger.debug('Retrieved users:', users.length);
      return users;
    } catch (error) {
      logger.error(
        'Failed to get users:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Obtenir un utilisateur par ID
   */
  async getUserById(id: number | string): Promise<User> {
    try {
      // ✅ UTILISE NOUVELLE API : getExtracted (extraction automatique)
      const user = await this.apiService.getExtracted<User>(`/api/users/${id}`);

      logger.debug('Retrieved user:', id);
      return user;
    } catch (error) {
      // ✅ UTILISE NOUVEL HELPER : handleServiceError (logging centralisé)
      throw handleServiceError(error, `Failed to get user ${id}`, 'UnifiedUserService');
    }
  }

  /**
   * Créer un nouvel utilisateur
   */
  async createUser(request: UserCreateRequest): Promise<User> {
    try {
      logger.info('Creating user:', request);

      // ✅ UTILISE NOUVELLE API : postExtracted (extraction automatique)
      const user = await this.apiService.postExtracted<User, UserCreateRequest>('/api/users', request);

      logger.info('User created successfully:', user);
      return user;
    } catch (error) {
      // ✅ UTILISE NOUVEL HELPER : handleServiceError (logging centralisé)
      throw handleServiceError(error, 'Failed to create user', 'UnifiedUserService');
    }
  }

  /**
   * Mettre à jour un utilisateur
   */
  async updateUser(id: number | string, request: UserUpdateRequest): Promise<User> {
    try {
      logger.info('Updating user:', { id, request });

      const response = await this.apiService.patch<{ status: string; data: User }>(`/api/users/${id}`, request);

      logger.info('User updated successfully:', response.data);
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to update user:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Supprimer un utilisateur
   */
  async deleteUser(id: number | string): Promise<void> {
    try {
      await this.apiService.delete(`/api/users/${id}`);

      logger.info('User deleted successfully:', id);
    } catch (error) {
      logger.error(
        'Failed to delete user:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Activer/désactiver un utilisateur
   */
  async toggleUserStatus(id: number | string, isActive: boolean): Promise<User> {
    try {
      const response = await this.apiService.patch<{ status: string; data: User }>(`/api/users/${id}/status`, { isActive });

      logger.info('User status updated:', { id, isActive });
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to update user status:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Obtenir les statistiques des utilisateurs
   */
  async getUserStats(): Promise<UserStats> {
    try {
      const response = await this.apiService.get<{ status: string; data: UserStats }>('/api/users/stats');

      logger.debug('Retrieved user stats:', response.data);
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to get user stats:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Réinitialiser le mot de passe d'un utilisateur
   */
  async resetUserPassword(id: number | string, newPassword: string): Promise<void> {
    try {
      await this.apiService.post(`/api/users/${id}/reset-password`, {
        password: newPassword,
      });

      logger.info('Password reset successfully for user:', id);
    } catch (error) {
      logger.error(
        'Failed to reset user password:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Obtenir les préférences d'un utilisateur
   */
  async getUserPreferences(userId?: number | string): Promise<UserPreferences> {
    try {
      const id = userId || this.currentUser?.id;
      if (!id) throw new Error('User ID required');

      const response = await this.apiService.get<{ status: string; data: UserPreferences }>(`/api/users/${id}/preferences`);

      this.userPreferences = response.data;
      logger.debug('Retrieved user preferences:', id);
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to get user preferences:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Mettre à jour les préférences d'un utilisateur
   */
  async updateUserPreferences(
    preferences: Partial<UserPreferences>,
    userId?: number | string
  ): Promise<UserPreferences> {
    try {
      const id = userId || this.currentUser?.id;
      if (!id) throw new Error('User ID required');

      const response = await this.apiService.patch<{ status: string; data: UserPreferences }>(`/api/users/${id}/preferences`, preferences);

      this.userPreferences = { ...this.userPreferences, ...response.data };
      logger.info('User preferences updated:', { id, preferences });
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to update user preferences:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Réinitialiser les préférences d'un utilisateur
   */
  async resetUserPreferences(userId?: number | string): Promise<UserPreferences> {
    try {
      const id = userId || this.currentUser?.id;
      if (!id) throw new Error('User ID required');

      const response = await this.apiService.post<{ status: string; data: UserPreferences }>(`/api/users/${id}/preferences/reset`);

      this.userPreferences = response.data;
      logger.info('User preferences reset:', id);
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to reset user preferences:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Définir l'utilisateur courant
   */
  setCurrentUser(user: User): void {
    this.currentUser = user;
    logger.debug('Current user set:', user.id);
  }

  /**
   * Obtenir l'utilisateur courant
   */
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Obtenir les préférences de l'utilisateur courant
   */
  getCurrentUserPreferences(): UserPreferences | null {
    return this.userPreferences;
  }

  /**
   * Mettre à jour le profil de l'utilisateur courant
   */
  async updateCurrentUserProfile(request: Partial<UserUpdateRequest>): Promise<User> {
    // Fallback de sécurité : si currentUser est null mais présent dans le store Zustand
    let userToUpdate = this.currentUser;
    if (!userToUpdate) {
      const storeUser = useAuthStore.getState().user;
      if (storeUser) {
        userToUpdate = storeUser;
        this.currentUser = storeUser;
        logger.debug('UnifiedUserService: currentUser restored from AuthStore fallback');
      } else {
        throw new Error('No current user');
      }
    }

    const updatedUser = await this.updateUser(userToUpdate.id, request);
    this.setCurrentUser(updatedUser);
    return updatedUser;
  }

  /**
   * Mettre à jour les préférences de l'utilisateur courant
   */
  async updateCurrentUserPreferences(
    preferences: Partial<UserPreferences>
  ): Promise<UserPreferences> {
    if (!this.currentUser) throw new Error('No current user');

    return this.updateUserPreferences(preferences, this.currentUser.id);
  }

  /**
   * Obtenir les utilisateurs par rôle (méthode spécifique pour compatibilité)
   */
  async getUsersByRole(role: 'PATIENT' | 'DOCTOR' | 'ADMIN'): Promise<User[]> {
    return this.getUsers({ role });
  }

  /**
   * Obtenir les médecins par spécialité
   */
  async getDoctorsBySpecialty(specialty: string): Promise<User[]> {
    try {
      const response = await this.apiService.get<{ status: string; data: User[] }>(
        `/api/doctors/specialty/${encodeURIComponent(specialty)}`
      );

      logger.debug('Retrieved doctors by specialty:', { specialty, count: response.data.length });
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to get doctors by specialty:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Obtenir les médecins disponibles
   */
  async getAvailableDoctors(): Promise<User[]> {
    try {
      const response = await this.apiService.get<{ status: string; data: User[] }>('/api/doctors/available');

      logger.debug('Retrieved available doctors:', response.data.length);
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to get available doctors:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Mettre à jour la disponibilité d'un médecin
   */
  async updateDoctorAvailability(doctorId: number | string, isAvailable: boolean): Promise<User> {
    try {
      const response = await this.apiService.patch<{ status: string; data: User }>(`/api/doctors/${doctorId}/availability`, {
        isAvailable,
      });

      logger.info('Doctor availability updated:', { doctorId, isAvailable });
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to update doctor availability:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Obtenir l'historique de connexion d'un utilisateur
   */
  async getUserLoginHistory(
      userId: number | string,
      limit?: number
    ): Promise<
    {
      loginTime: string;
      ipAddress: string;
      userAgent: string;
      location?: string;
    }[]
  > {
    try {
      const params = limit ? `?limit=${limit}` : '';
      const response = await this.apiService.get<{ status: string; data: Array<{ loginTime: string; ipAddress: string; userAgent: string; location?: string }> }>(`/api/users/${userId}/login-history${params}`);

      logger.debug('Retrieved user login history:', { userId, count: response.data.length });
      return response.data;
    } catch (error) {
      logger.error(
        'Failed to get user login history:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Exporter les données utilisateurs
   */
  async exportUsers(filters?: UserFilters, format: 'CSV' | 'JSON' | 'PDF' = 'CSV'): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (filters?.role) params.append('role', filters.role);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.speciality) params.append('specialty', filters.speciality);
      if (filters?.search) params.append('search', filters.search);
      params.append('format', format);

      const response = await this.apiService.get<Blob>(`/api/users/export?${params}`, {
        responseType: 'blob',
      });

      logger.info('Exported users:', { format, filters });
      return response;
    } catch (error) {
      logger.error(
        'Failed to export users:',
        (error as { message?: string }).message || String(error)
      );
      throw error;
    }
  }

  /**
   * Valider les données utilisateur
   */
  validateUserData(data: Partial<UserCreateRequest>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.push('Invalid email format');
    }

    if (data.name && data.name.length < 2) {
      errors.push('Name must be at least 2 characters');
    }

    if (data.phone && !/^[+]?[\d\s\-()]+$/.test(data.phone)) {
      errors.push('Invalid phone format');
    }

    if (data.role && !['PATIENT', 'DOCTOR', 'ADMIN'].includes(data.role)) {
      errors.push('Invalid role');
    }

    // Validation des profils selon le rôle (champs aplatis)
    if (data.speciality) {
      if (data.speciality.length < 2) {
        errors.push('Doctor specialty must be at least 2 characters');
      }
    }
    if (data.licenseNumber) {
      if (data.licenseNumber.length < 5) {
        errors.push('Doctor license number must be at least 5 characters');
      }
    }
    if (data.experienceYears !== undefined && data.experienceYears < 0) {
      errors.push('Doctor experience years cannot be negative');
    }
    if (data.consultationFee !== undefined && data.consultationFee < 0) {
      errors.push('Doctor consultation fee cannot be negative');
    }

    if (data.dateOfBirth && data.dateOfBirth > new Date()) {
      errors.push('Patient date of birth cannot be in the future');
    }
    if (data.bloodType && !['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].includes(data.bloodType)) {
      errors.push('Invalid blood type');
    }
    if (data.gender && !['M', 'F', 'Other'].includes(data.gender)) {
      errors.push('Invalid gender');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Nettoyer les données utilisateur
   */
  cleanup() {
    this.currentUser = null;
    this.userPreferences = null;
    logger.debug('User service cleaned up');
  }
}

// Export singleton
const unifiedUserService = UnifiedUserService.getInstance();

export default unifiedUserService;
export { unifiedUserService as userService };
